import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
    authService,
    type GoogleAdsAccountsResponse,
} from '@common/api/authService';
import { useTranslation } from '@client-app/i18n';
import { CircleNotch } from '@phosphor-icons/react';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    buildGoogleAdsRows,
    createGoogleAdsAccountRequestScope,
    getAccountName,
    isSameGoogleAdsAccountRequestScope,
    normalizeCustomerId,
    type GoogleAdsAccountRequestScope,
    type GoogleAdsRow,
} from '@/pages/GoogleAdsAccountSelection/googleAdsAccountSelectionModel';
import Modal from '../Modal';

interface GoogleAdsAccountSelectionModalProps {
    isOpen: boolean;
    connectionId?: string | null;
    workspaceId?: string | null;
    onClose: () => void;
    onSaved?: () => void | Promise<void>;
}

const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) {
        return error.message;
    }
    return fallback;
};

interface ScopedGoogleAdsAccountsResponse {
    scope: GoogleAdsAccountRequestScope;
    value: GoogleAdsAccountsResponse;
}

export default function GoogleAdsAccountSelectionModal({
    isOpen,
    connectionId,
    workspaceId,
    onClose,
	onSaved,
}: GoogleAdsAccountSelectionModalProps) {
    const { t } = useTranslation();
    const { currentWorkspace } = useAuth();
    const { showToast } = useToast();
    const [scopedResponse, setScopedResponse] = useState<ScopedGoogleAdsAccountsResponse | null>(null);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const resolvedWorkspaceId = workspaceId || currentWorkspace?.id || null;
    const requestGenerationRef = useRef(0);
    const activeRequestScopeRef = useRef<GoogleAdsAccountRequestScope | null>(null);
    const response = scopedResponse
        && activeRequestScopeRef.current
        && scopedResponse.scope.workspaceId === resolvedWorkspaceId
        && scopedResponse.scope.connectionId === (connectionId || null)
        && isSameGoogleAdsAccountRequestScope(scopedResponse.scope, activeRequestScopeRef.current)
        ? scopedResponse.value
        : null;

    const axiosRequestConfig = useMemo(
        () => resolvedWorkspaceId
            ? { headers: { 'X-Workspace-Id': resolvedWorkspaceId } }
            : undefined,
        [resolvedWorkspaceId],
    );

    const loadAccounts = useCallback(async () => {
        if (!resolvedWorkspaceId || !isOpen) {
            return;
        }

        const requestScope = createGoogleAdsAccountRequestScope(
            resolvedWorkspaceId,
            connectionId,
            requestGenerationRef.current + 1,
        );
        requestGenerationRef.current = requestScope.generation;
        activeRequestScopeRef.current = requestScope;
        const isActiveRequest = () => isSameGoogleAdsAccountRequestScope(
            activeRequestScopeRef.current,
            requestScope,
        );

        setIsLoading(true);
        setErrorMessage(null);
        setScopedResponse(null);
        setSelectedCustomerIds(new Set());
        try {
            const nextResponse = await authService.getNangoGoogleAdsAccounts({
                connectionId: connectionId || undefined,
                axiosRequestConfig,
            });
            if (isActiveRequest()) {
                setScopedResponse({ scope: requestScope, value: nextResponse });
                setSelectedCustomerIds(new Set(nextResponse.defaultSelectedCustomerIds || []));
            }
        } catch (error) {
            if (isActiveRequest()) {
                setErrorMessage(getErrorMessage(
                    error,
                    t('website.integrationsSettings.googleAdsAccountSelection.updateFailed'),
                ));
            }
        } finally {
            if (isActiveRequest()) {
                setIsLoading(false);
            }
        }
    }, [axiosRequestConfig, connectionId, isOpen, resolvedWorkspaceId, t]);

    useEffect(() => {
        if (!isOpen) {
            requestGenerationRef.current += 1;
            activeRequestScopeRef.current = null;
            setScopedResponse(null);
            setSelectedCustomerIds(new Set());
            setErrorMessage(null);
            setIsLoading(false);
            setIsSaving(false);
            return undefined;
        }

        void loadAccounts();

        return () => {
            requestGenerationRef.current += 1;
            activeRequestScopeRef.current = null;
        };
    }, [isOpen, loadAccounts]);

    const rows = useMemo<GoogleAdsRow[]>(
        () => buildGoogleAdsRows(response?.accounts || []),
        [response?.accounts],
    );

    const eligibleRows = useMemo(
        () => rows.filter((row) => row.eligible),
        [rows],
    );
    const eligibleCustomerIds = useMemo(
        () => eligibleRows.map((row) => normalizeCustomerId(row.id)).filter(Boolean),
        [eligibleRows],
    );
    const selectedEligibleCustomerIds = useMemo(
        () => eligibleCustomerIds.filter((customerId) => selectedCustomerIds.has(customerId)),
        [eligibleCustomerIds, selectedCustomerIds],
    );
    const selectedEligibleCount = selectedEligibleCustomerIds.length;
    const areAllEligibleSelected = eligibleCustomerIds.length > 0
        && selectedEligibleCount === eligibleCustomerIds.length;
    const hasPartialEligibleSelection = selectedEligibleCount > 0 && !areAllEligibleSelected;

    const toggleCustomer = (customerId: string) => {
        setSelectedCustomerIds((previous) => {
            const next = new Set(previous);
            if (next.has(customerId)) {
                next.delete(customerId);
            } else {
                next.add(customerId);
            }
            return next;
        });
    };

    const selectAllEligible = () => {
        setSelectedCustomerIds(new Set(eligibleCustomerIds));
    };

    const toggleAllEligible = () => {
        if (areAllEligibleSelected) {
            setSelectedCustomerIds(new Set());
            return;
        }
        selectAllEligible();
    };

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        onClose();
    };

    const saveSelection = async () => {
        const resolvedConnectionId = response?.connectionId || connectionId;
        if (!resolvedWorkspaceId || !response?.hasIntegration || !resolvedConnectionId) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        try {
            await authService.saveNangoGoogleAdsAccountSelection({
                connection_id: resolvedConnectionId,
                selected_customer_ids: selectedEligibleCustomerIds,
                axiosRequestConfig,
            });
            await onSaved?.();
            showToast(t('website.integrationsSettings.googleAdsAccountSelection.updateSuccess'), 'success');
            onClose();
        } catch (error) {
            setErrorMessage(getErrorMessage(error, t('website.integrationsSettings.googleAdsAccountSelection.updateFailed')));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('website.integrationsSettings.googleAdsAccountSelection.title')}
            width="max-w-[760px]"
            bodyClassName="px-6 pb-6 pt-1"
            titleClassName="text-[#1A162F]"
        >
            <div className="space-y-4">
                {errorMessage ? (
                    <div className="rounded-[10px] border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {errorMessage}
                    </div>
                ) : null}

                <div className="min-h-[280px] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white">
	                    {isLoading ? (
	                        <div className="flex min-h-[280px] items-center justify-center text-sm text-[#667085]">
	                            <CircleNotch size={18} className="mr-2 animate-spin" />
	                            {t('website.integrationsSettings.googleAdsAccountSelection.loadingAccounts')}
	                        </div>
	                    ) : !response?.hasIntegration ? (
	                        <div className="flex min-h-[280px] items-center justify-center px-6 text-center text-sm text-[#667085]">
	                            {t('website.integrationsSettings.googleAdsAccountSelection.noActiveConnection')}
	                        </div>
                    ) : (
                        <div className="max-h-[360px] overflow-auto">
                            <table className="min-w-full table-fixed border-collapse text-left text-xs">
                                <colgroup>
                                    <col className="w-[64%]" />
                                    <col className="w-[36%]" />
                                </colgroup>
                                <thead className="sticky top-0 z-[1]">
                                    <tr className="border-b border-gray-100 bg-gray-50/95">
                                        <th className="px-4 py-3 text-xs font-medium text-text-sub">
                                            <label className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
	                                                    checked={areAllEligibleSelected}
	                                                    disabled={isLoading || eligibleCustomerIds.length === 0}
	                                                    aria-label={t('website.integrationsSettings.googleAdsAccountSelection.selectAllCustomers')}
                                                    ref={(input) => {
                                                        if (input) {
                                                            input.indeterminate = hasPartialEligibleSelection;
                                                        }
                                                    }}
                                                    onChange={toggleAllEligible}
                                                    className="h-4 w-4 shrink-0 rounded border-[#D0D5DD] text-[#1A162F] focus:ring-[#1A162F]"
                                                />
	                                                <span>{t('website.integrationsSettings.googleAdsAccountSelection.customer')}</span>
	                                            </label>
	                                        </th>
	                                        <th className="px-4 py-3 text-xs font-medium text-text-sub">{t('website.integrationsSettings.googleAdsAccountSelection.customerId')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {eligibleRows.map((account) => {
                                        const customerId = normalizeCustomerId(account.id);
                                        const isSelected = selectedCustomerIds.has(customerId);
                                        return (
                                            <tr
                                                key={customerId}
                                                className="transition-colors hover:bg-gray-50/40"
                                            >
                                                <td className="px-4 py-3">
                                                    <label className="flex min-w-0 items-center gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleCustomer(customerId)}
                                                            className="h-4 w-4 shrink-0 rounded border-[#D0D5DD] text-[#1A162F] focus:ring-[#1A162F]"
                                                        />
                                                        <span className="min-w-0">
                                                            <span className="block truncate font-medium text-[#1A162F]">
                                                                {getAccountName(account)}
                                                            </span>
                                                            <span className="mt-1 block truncate text-xs text-[#667085]">
                                                                {account.currency_code || '-'}
                                                                {' / '}
                                                                {account.time_zone || account.timezone || '-'}
                                                            </span>
                                                        </span>
                                                    </label>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-[#475467]">
                                                    {customerId}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {eligibleRows.length === 0 ? (
                                        <tr>
	                                            <td colSpan={2} className="px-4 py-12 text-center text-sm text-[#667085]">
	                                                {t('website.integrationsSettings.googleAdsAccountSelection.noEligibleAccounts')}
	                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-1">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSaving}
                        className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                    >
	                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={saveSelection}
                        disabled={isLoading || isSaving || !response?.hasIntegration}
                        className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-5 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isSaving ? (
                            <CircleNotch size={14} className="animate-spin" />
                        ) : null}
	                        <span>{t('website.integrationsSettings.googleAdsAccountSelection.save')}</span>
                    </button>
                </div>
            </div>
        </Modal>
    );
}
